import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Key, Plus, ShoppingCart } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

export function ActivationCredits() {
  const { t, shopEnabled } = useApp();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/activation/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener("sb:activation-changed", onChange);
    window.addEventListener("focus", onChange);
    return () => { window.removeEventListener("sb:activation-changed", onChange); window.removeEventListener("focus", onChange); };
  }, [load]);

  if (!status || !status.required) return null;
  const credits = status.credits || 0;

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/activation/redeem", { code: code.trim() });
      toast.success(r.data.message || t("codeRedeemed"));
      setCode(""); setOpen(false); load();
      window.dispatchEvent(new Event("sb:activation-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <div className="sb-card p-4 mt-5" data-testid="activation-credits-card">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "color-mix(in srgb, var(--app-primary) 18%, transparent)" }}>
          <Key size={24} weight="duotone" style={{ color: "var(--app-strong)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold leading-tight">{t("activationsTitle")}</p>
          <p className="font-body text-sm text-gray-500" data-testid="activation-credits-count">
            <span className="font-heading font-bold text-base" style={{ color: "var(--app-strong)" }}>{credits}</span> {t("activationsAvailableWord")}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button data-testid="redeem-open-btn" onClick={() => setOpen(true)} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1"><Plus size={16} weight="bold" />{t("redeemCodeBtn")}</button>
        {shopEnabled && <button data-testid="buy-activation-btn" onClick={() => navigate("/shop?buy=activation")} className="sb-btn px-3 py-2 text-white text-sm font-medium flex items-center gap-1" style={{ background: "var(--app-primary)" }}><ShoppingCart size={16} weight="bold" />{t("buyActivation")}</button>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="redeem-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("redeemTitle")}</DialogTitle>
            <DialogDescription>{t("activationCodeHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input data-testid="redeem-dialog-input" className="sb-input" placeholder="SBXXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value)} />
            <button data-testid="redeem-dialog-btn" onClick={redeem} disabled={busy || !code.trim()} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>{t("redeem")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
